// Cart drawer "Choose your free bundle" picker.
//
// The picker is always visible. Below the threshold it renders locked - cards are still
// pickable, the Add button is disabled and shows how much more is needed.
// Threshold reached -> the default bundle (Theme settings > Free Bundle Picker) is added
// free automatically. Remove that line and the Add button unlocks so the shopper can pick
// a different bundle; once a bundle is in the cart the button is hidden again.
// Threshold lost -> the bundle line is removed and the block falls back to its locked state.
//
// Loaded after cart.js so window.refreshCartDrawer / setFreebieLoading already exist.

// Session keys: the manual-removal flag (blocks re-auto-adding) and the shopper's card choice.
var BP_DISMISSED_KEY = 'bundleGiftDismissed';
var BP_CHOICE_KEY = 'bundleGiftChoice';

// Guard so a cart refresh mid-add can't fire a second add/remove for the same change.
window._bundlePickerBusy = false;

function bpReadFlag(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function bpWriteFlag(key, value) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (e) {}
}

// Eligible cart value: the cart total MINUS the bundle's own line. Device, Combo and the
// other freebie lines all still count - a deliberate difference from the free-gift
// handlers in cart.js, which strip Device/Combo out of theirs.
//
// The bundle is excluded from its own threshold so that a priced bundle cannot hold the
// cart above the line that granted it. Counting it would mean a GBP50 cart unlocking a
// GBP20 bundle, reaching GBP70, and then staying qualified even after every other item
// was removed - the shopper checks out with the bundle alone. Excluding it also makes the
// state stable: adding or removing the bundle never changes whether it is earned.
//
// Subtracts on the same basis it measures - original line prices against the subtotal,
// final ones against the total - so the two never mix.
//
// Keep this in step with the same calculation in cart-drawer-bundle-picker.liquid,
// or the server's first paint will disagree with the first sync.
function bpEligibleTotal(cart, onSubtotal) {
  var total = (onSubtotal ? cart.original_total_price : cart.total_price) / 100;
  cart.items.forEach(function (item) {
    if (item.properties && item.properties._bundle_gift === 'true') {
      var line = onSubtotal ? item.original_line_price : item.final_line_price;
      total -= (line || 0) / 100;
    }
  });
  return total;
}

// Shopper-facing amount for the locked button copy, matching apply-discount.js.
function bpMoney(amount) {
  // Whole amounts drop the decimals, matching the money_without_trailing_zeros filter the
  // Liquid uses - otherwise the badge would jump from GBP55 to GBP55.00 on the first sync.
  var digits = amount % 1 === 0 ? 0 : 2;
  try {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch (e) {
    return amount.toFixed(digits);
  }
}

// The bundle line currently in the cart, if any. Matched on the _bundle_gift property
// rather than variant id, so a bundle later removed from Theme settings is still cleaned up.
function bpFindLine(cart) {
  return cart.items.find(function (item) {
    return item.properties && item.properties._bundle_gift === 'true';
  });
}

// Paint the picker: which card is selected, the "<name> bundle selected" line, and what
// the Add button offers. Called on every sync, since the drawer re-renders.
// `remaining` is how much more the cart needs; anything above 0 paints the locked state.
// `eligibleTotal` drives the product-page progress bar.
function bpRender(root, inCartVariantId, remaining, eligibleTotal) {
  var cards = root.querySelectorAll('.cd-bundle__card');
  if (!cards.length) return;

  // Stashed on the root so the delegated click/change handlers can repaint without a cart fetch.
  var locked = (remaining || 0) > 0;
  root.classList.toggle('is-below-threshold', locked);
  root.setAttribute('data-bp-remaining', locked ? String(remaining) : '');
  root.setAttribute('data-bp-eligible', String(eligibleTotal || 0));

  // Progress towards the threshold, capped so an over-threshold cart still reads as full.
  var progressEl = root.querySelector('.cd-bundle__progress');
  if (progressEl) {
    var thresholdForBar = parseFloat(root.getAttribute('data-bp-threshold')) || 0;
    var percent = thresholdForBar > 0 ? ((eligibleTotal || 0) / thresholdForBar) * 100 : 100;
    percent = Math.max(0, Math.min(100, Math.round(percent)));
    progressEl.setAttribute('aria-valuenow', String(percent));
    var fillEl = progressEl.querySelector('.cd-bundle__progress-fill');
    if (fillEl) fillEl.style.width = percent + '%';
  }

  // Badge reads "UNLOCK AT ..." below the threshold and "UNLOCKED AT ..." once met. Both
  // templates ship raw so the shortfall can be re-substituted as the cart changes.
  var badgeEl = root.querySelector('.cd-bundle__badge');
  if (badgeEl) {
    var badgeText = locked
      ? root.getAttribute('data-bp-badge-locked-text') || ''
      : root.getAttribute('data-bp-badge-text') || '';
    if (badgeText) {
      var thresholdValue = parseFloat(root.getAttribute('data-bp-threshold')) || 0;
      badgeText = badgeText
        .replace('threshold_money', bpMoney(thresholdValue))
        .replace('remaining_money', bpMoney(remaining || 0));
    }
    badgeEl.textContent = badgeText;
    badgeEl.hidden = !badgeText;
  }

  // Nothing is selected until the cart qualifies. Above the threshold the priority is
  // what is actually in the cart, else the shopper's own pick, else the Auto-added Bundle
  // from Theme settings.
  var selectedId = 0;
  if (!locked) {
    selectedId = inCartVariantId;
    if (!selectedId) {
      var stored = parseInt(bpReadFlag(BP_CHOICE_KEY), 10);
      if (stored) selectedId = stored;
    }
    if (!selectedId) {
      var defaultIndex = root.getAttribute('data-bp-default') || '3';
      var defaultCard = root.querySelector('.cd-bundle__card[data-bp-index="' + defaultIndex + '"]') || cards[0];
      selectedId = parseInt(defaultCard.getAttribute('data-bp-variant-id'), 10);
    }

    // A stored/default id can point at a bundle that has since been removed from settings.
    if (!root.querySelector('.cd-bundle__card[data-bp-variant-id="' + selectedId + '"]')) {
      selectedId = parseInt(cards[0].getAttribute('data-bp-variant-id'), 10);
    }
  }

  // Cards are locked while a bundle is in the cart - the shopper removes it to swap,
  // which is what makes the Add button reappear. Kept on the root so the delegated
  // handlers can read it back after a drawer re-render.
  // Also locked while signed out with a login required: picking is pointless there, since signing in auto-adds the configured bundle rather than whatever was clicked.
  var needsLoginPaint = root.getAttribute('data-bp-needs-login') === 'true';
  var cardsLocked = !!inCartVariantId || needsLoginPaint;
  root.setAttribute('data-bp-in-cart', inCartVariantId || '');
  root.classList.toggle('is-locked', cardsLocked);

  // The product page is display-only, so it gets no selected highlight - every card looks
  // the same there. The title is still tracked, because the note names the picked bundle.
  var displayOnly = root.classList.contains('cd-bundle--pdp');

  var selectedTitle = '';
  // Struck-through worth of the picked card, quoted by the unlocked banner.
  var selectedValue = '';
  cards.forEach(function (card) {
    var isSelected = parseInt(card.getAttribute('data-bp-variant-id'), 10) === selectedId;
    card.classList.toggle('is-selected', isSelected && !displayOnly);
    var radio = card.querySelector('.cd-bundle__radio');
    if (radio) {
      radio.checked = isSelected;
      radio.disabled = cardsLocked || locked;
    }
    // The note can name a bundle differently from its card - a card reading 'Female'
    // giving 'Women’s bundle selected'. Falls back to the card title when unset.
    if (isSelected) {
      selectedTitle = card.getAttribute('data-bp-selected-label') || card.getAttribute('data-bp-title') || '';
      selectedValue = card.getAttribute('data-bp-value') || '';
    }
  });

  // "Male bundle selected" - names the picked card whether or not it is in the cart yet,
  // so the shopper can always see which bundle the Add button would give them.
  var selectedTemplate = root.getAttribute('data-bp-selected-text') || '';
  var selectedEl = root.querySelector('.cd-bundle__selected');
  if (selectedEl) {
    if (selectedTitle && selectedTemplate) {
      selectedEl.textContent = selectedTemplate.replace('bundle_title', selectedTitle);
      selectedEl.hidden = false;
    } else {
      selectedEl.hidden = true;
    }
  }

  // Product page shows a note instead of a dead disabled button: the shortfall below the
  // threshold, the picked bundle once it is in the basket, nothing while Add is offered.
  var noteEl = root.querySelector('.cd-bundle__note');
  if (noteEl) {
    var noteText = '';
    if (locked) {
      var noteTemplate = root.getAttribute('data-bp-note-text') || '';
      if (noteTemplate) {
        noteText = noteTemplate
          .replace('remaining_money', bpMoney(remaining))
          .replace('threshold_money', bpMoney(parseFloat(root.getAttribute('data-bp-threshold')) || 0));
      }
    } else if (inCartVariantId && selectedTemplate) {
      noteText = selectedTemplate.replace('bundle_title', selectedTitle);
    }
    noteEl.textContent = noteText;
    noteEl.hidden = !noteText;
  }
  // Add button has three states: hidden while a bundle is in the cart, disabled with the
  // shortfall below the threshold, and live once the cart qualifies. The product page has
  // no button at all, so this is a no-op there.
  // Signed out (with login required) the button explains why and cannot be pressed - needsLoginPaint is read further up, where the cards are locked for the same reason.
  var addBtn = root.querySelector('.cd-bundle__add');
  if (addBtn) {
    addBtn.hidden = !!inCartVariantId;
    addBtn.disabled = !!inCartVariantId || locked || needsLoginPaint;
    addBtn.classList.remove('is-loading');
    // Same order as the Liquid, or the server's first paint and this one would disagree: the shortfall wins below the threshold, and the login prompt only once it is met.
    if (inCartVariantId) {
      addBtn.textContent = root.getAttribute('data-bp-added-text') || 'Added';
    } else if (locked) {
      var lockedText = root.getAttribute('data-bp-locked-text') || '';
      addBtn.textContent = lockedText
        ? lockedText.replace('remaining_money', bpMoney(remaining))
        : root.getAttribute('data-bp-add-text') || 'Add to cart';
    } else if (needsLoginPaint && root.getAttribute('data-bp-login-text')) {
      addBtn.textContent = root.getAttribute('data-bp-login-text');
    } else {
      addBtn.textContent = root.getAttribute('data-bp-add-text') || 'Add to cart';
    }
  }

  // Banner is drawer-only and there is just one of it, so the product page's pass is skipped
  // rather than letting both instances fight over the same element.
  if (!displayOnly) {
    bpRenderBanner(root, locked, inCartVariantId, needsLoginPaint, selectedTitle, selectedValue);
  }
}

// Paint the drawer's "Free bundle unlocked" banner, which sits above the line items and
// scrolls down to the cards. Shown ONLY once the cart qualifies and nothing has been picked
// yet - that is its whole job, so a bundle already in the cart, a cart under the threshold
// or a signed-out shopper who must log in all hide it again.
// Off unless the Theme setting is on, in which case the snippet renders nothing and this is a no-op.
function bpRenderBanner(root, locked, inCartVariantId, needsLogin, title, value) {
  var banner = document.getElementById('cart-drawer-bundle-banner');
  if (!banner) return;

  var show = !locked && !inCartVariantId && !needsLogin;
  banner.hidden = !show;
  // Left exactly as it was while hidden, so nothing is recomputed for a banner nobody sees.
  if (!show) return;

  // Both strings ship raw so the picked bundle can be re-substituted as the shopper clicks between cards.
  var titleEl = banner.querySelector('.cd-bpb__title');
  if (titleEl) {
    var titleText = banner.getAttribute('data-bpb-title') || '';
    var thresholdValue = parseFloat(root.getAttribute('data-bp-threshold')) || 0;
    titleEl.textContent = titleText.replace('threshold_money', bpMoney(thresholdValue));
  }

  var subEl = banner.querySelector('.cd-bpb__sub');
  if (subEl) {
    var subText = banner.getAttribute('data-bpb-text') || '';
    // Blanked when the picked card has no worth set, rather than leaving 'worth bundle_value' on screen.
    if (subText && subText.indexOf('bundle_value') !== -1 && !value) subText = '';
    if (subText) subText = subText.replace('bundle_value', value).replace('bundle_title', title);
    subEl.textContent = subText;
    subEl.hidden = !subText;
  }
}

// Re-render whichever cart UI is on the page.
function bpRefresh() {
  if (document.querySelector('cart-items')) return window.refreshCart?.();
  return window.refreshCartDrawer?.();
}

// Add one bundle variant to the cart as a freebie line.
function bpAddToCart(variantId) {
  // Marks the cartUpdate chain's cart stale, so a manual Add pressed mid-chain can't leave a later handler measuring its threshold against a cart that no longer has this line in it.
  window._freebieCartDirty = true;
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: {
        _bundle_gift: 'true',
        _bundle_auto: 'true',
        _bundle_uid: Date.now().toString(),
      },
    }),
  });
}

// Evaluate the picker against the live cart: auto-add, clean up, and repaint.
// Pass a cart object to reuse it; omit it and a fresh /cart.js is fetched.
// `inChain` MUST be passed true only by a caller that runs this sequentially AFTER the
// other freebie handlers have finished - cart.js's cartUpdate chain, its DOMContentLoaded
// sync, and Buy Now. Those are the only places the bundle may add or remove a line.
// Every other caller (refreshCart, refreshCartDrawer, clicks) paints and nothing more:
// mutating from there runs concurrently with handleGiftProduct/handleRotatingGift, which
// then measure their thresholds against a cart that is changing underneath them.
window.syncCartDrawerBundle = async function (cartState, inChain) {
  // Every product page renders the drawer as well as its own picker, so this works over
  // ALL instances: the cart is mutated once, then every instance is painted from it.
  var roots = document.querySelectorAll('.cd-bundle');
  if (!roots.length) return;

  // Only a MUTATING pass takes the lock, and only another mutating pass is turned away by
  // it. Paint-only passes never take it and never bail on it.
  //
  // Previously every pass took the lock, which silently swallowed threshold-drop removals:
  // a gift handler mid-chain calls refreshCart, refreshCart kicks off a paint-only sync,
  // that sync is still awaiting its /cart.js when the chain reaches its own mutating call
  // - so the mutating call hit the lock, returned, and the bundle was never removed even
  // though the cart was under the threshold.
  if (inChain) {
    if (window._bundlePickerBusy) return;
    window._bundlePickerBusy = true;
  }

  try {
    var cart = cartState;
    if (!cart || !cart.items) {
      cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .catch(function () {
          return null;
        });
    }
    if (!cart || !cart.items) return;

    // All instances carry the same Theme settings, so the config comes off the first one.
    var config = roots[0];
    var threshold = parseFloat(config.getAttribute('data-bp-threshold')) || 0;
    var onSubtotal = config.getAttribute('data-bp-on-subtotal') === 'true';
    // Defaults to on, so an unset setting keeps the original behaviour.
    var autoAdd = config.getAttribute('data-bp-auto-add') !== 'false';
    // Server-rendered: true only while the shopper is signed out AND login is required.
    var needsLogin = config.getAttribute('data-bp-needs-login') === 'true';
    var eligibleTotal = bpEligibleTotal(cart, onSubtotal);
    var thresholdMet = threshold <= 0 || eligibleTotal >= threshold;
    var remaining = Math.max(0, threshold - eligibleTotal);
    var line = bpFindLine(cart);

    // Signed out while a login is required, but a bundle is still in the cart - it was earned by a session that has since logged out, so take it back before anything else is decided. Checked ahead of the threshold because it applies however much is in the cart. The dismissed flag is deliberately left alone: logging out is not a refusal of the bundle.
    if (needsLogin && line && inChain) {
      roots.forEach(function (root) {
        bpRender(root, 0, remaining, eligibleTotal);
      });
      window.setFreebieLoading?.(true);
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
        await new Promise(function (r) {
          setTimeout(r, 120);
        });
        // Released before the refresh, whose re-render calls back into this sync to repaint.
        window._bundlePickerBusy = false;
        await bpRefresh();
      } catch (e) {
        console.error('[BundlePicker] logged-out removal failed', e);
      } finally {
        window.setFreebieLoading?.(false);
      }
      return;
    }

    // Below the threshold: pull the freebie back out, fall back to the locked state (the
    // block stays on screen), and clear the manual-removal flag so the next qualifying
    // cart auto-adds the default again.
    if (!thresholdMet) {
      roots.forEach(function (root) {
        bpRender(root, 0, remaining, eligibleTotal);
      });
      bpWriteFlag(BP_DISMISSED_KEY, null);
      if (line && inChain) {
        window.setFreebieLoading?.(true);
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          });
          await new Promise(function (r) {
            setTimeout(r, 120);
          });
          // Released before the refresh, whose re-render calls back into this sync to repaint.
          window._bundlePickerBusy = false;
          await bpRefresh();
        } catch (e) {
          console.error('[BundlePicker] threshold-drop removal failed', e);
        } finally {
          window.setFreebieLoading?.(false);
        }
      }
      return;
    }

    // Threshold met and nothing added yet: auto-add the default, unless auto-add is off
    // (the shopper picks and presses Add), or they removed it themselves - in both cases
    // the Add button below is the only way in.
    if (autoAdd && !needsLogin && !line && bpReadFlag(BP_DISMISSED_KEY) !== '1' && inChain) {
      var cards = config.querySelectorAll('.cd-bundle__card');
      if (!cards.length) return;

      // Always the Auto-added Bundle from Theme settings, so crossing the threshold always
      // lands the same bundle. Picking a different one is a deliberate remove-then-add.
      var defaultIndex = config.getAttribute('data-bp-default') || '3';
      var defaultCard = config.querySelector('.cd-bundle__card[data-bp-index="' + defaultIndex + '"]') || cards[0];
      var autoId = parseInt(defaultCard.getAttribute('data-bp-variant-id'), 10);
      if (!autoId) return;

      window.setFreebieLoading?.(true);
      try {
        await bpAddToCart(autoId);
        bpWriteFlag(BP_CHOICE_KEY, String(autoId));
        await new Promise(function (r) {
          setTimeout(r, 120);
        });
        // Released before the refresh, whose re-render calls back into this sync to repaint.
        window._bundlePickerBusy = false;
        await bpRefresh();
      } catch (e) {
        console.error('[BundlePicker] auto-add failed', e);
      } finally {
        window.setFreebieLoading?.(false);
      }
      return;
    }

    var inCartId = line ? parseInt(line.variant_id, 10) : 0;
    roots.forEach(function (root) {
      bpRender(root, inCartId, 0, eligibleTotal);
    });
  } finally {
    if (inChain) window._bundlePickerBusy = false;
  }
};

// Repaint every instance from the state each one last recorded, so a click can update the
// drawer and the product-page picker together without re-reading the cart.
function bpRepaintAll() {
  document.querySelectorAll('.cd-bundle').forEach(function (root) {
    bpRender(
      root,
      parseInt(root.getAttribute('data-bp-in-cart'), 10) || 0,
      parseFloat(root.getAttribute('data-bp-remaining')) || 0,
      parseFloat(root.getAttribute('data-bp-eligible')) || 0
    );
  });
}

// Delegated handlers - the drawer re-renders on every cart change, so nothing is bound
// directly to the picker's own elements.
// True only where hover is unavailable, i.e. touch. Hybrid laptops answer for whichever
// input is in use, so this is read per click rather than cached.
function bpIsTouch() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}

// Close every open info popup except `keep`, and sync each button's aria-expanded.
function bpCloseTips(keep) {
  document.querySelectorAll('.cd-bundle__card.is-tip-open').forEach(function (card) {
    if (card === keep) return;
    card.classList.remove('is-tip-open');
    card.querySelector('.cd-bundle__info')?.setAttribute('aria-expanded', 'false');
  });
}

document.addEventListener('click', function (e) {
  // Unlocked banner: scrolls the drawer down to the cards and nothing more - the shopper
  // still picks a bundle and presses Add, which is the whole point of the auto-add-off setup.
  var bannerCta = e.target.closest('.cd-bpb__cta');
  if (bannerCta) {
    e.preventDefault();
    var picker = document.getElementById('cart-drawer-bundle');
    if (!picker) return;
    // scrollIntoView walks up to whichever ancestor actually scrolls, so this keeps working
    // without naming a container - the drawer already switches which element scrolls by viewport height.
    var reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    // Reflow between the remove and the add restarts the highlight when the button is pressed
    // again mid-animation, which otherwise does nothing at all the second time.
    picker.classList.remove('is-flash');
    void picker.offsetWidth;
    picker.classList.add('is-flash');
    setTimeout(function () {
      picker.classList.remove('is-flash');
    }, 1400);
    return;
  }

  // Info popup. Pointer devices open it on hover alone (pure CSS) - clicking must not
  // latch it open. Touch devices have no hover, so there the tap toggles it instead.
  // Handled before the card branch below, which would otherwise swallow the event.
  var infoBtn = e.target.closest('.cd-bundle__info');
  if (infoBtn) {
    e.preventDefault();
    if (!bpIsTouch()) {
      // Some browsers treat a mouse click as focus-visible, which would latch the panel
      // open via the keyboard rule and make it look like the card itself opens it.
      infoBtn.blur();
      return;
    }
    var infoCard = infoBtn.closest('.cd-bundle__card');
    if (infoCard) {
      var open = !infoCard.classList.contains('is-tip-open');
      bpCloseTips(null);
      infoCard.classList.toggle('is-tip-open', open);
      infoBtn.setAttribute('aria-expanded', String(open));
    }
    return;
  }

  // Let the View More link navigate, and close anything still open behind it.
  if (e.target.closest('.cd-bundle__tip-link')) {
    bpCloseTips(null);
    return;
  }

  // A click anywhere else dismisses an open popup.
  if (!e.target.closest('.cd-bundle__tip')) bpCloseTips(null);

  // Card click: selection only. Nothing hits the cart until Add is pressed. Picking is off
  // while a bundle is already added, and off entirely on the product page, where the cards
  // are display-only - the shopper chooses in the cart drawer.
  var card = e.target.closest('.cd-bundle__card');
  if (card) {
    var cardRoot = card.closest('.cd-bundle');
    if (!cardRoot || cardRoot.classList.contains('is-locked')) return;
    if (cardRoot.classList.contains('is-below-threshold')) return;
    if (cardRoot.classList.contains('cd-bundle--pdp')) return;
    var variantId = parseInt(card.getAttribute('data-bp-variant-id'), 10);
    if (variantId) {
      bpWriteFlag(BP_CHOICE_KEY, String(variantId));
      bpRepaintAll();
    }
    return;
  }

  // Add button: adds the selected bundle, then clears the manual-removal flag so the
  // usual auto-add rules apply again on later cart changes.
  var addBtn = e.target.closest('.cd-bundle__add');
  var root = addBtn ? addBtn.closest('.cd-bundle') : null;
  if (addBtn && root) {
    e.preventDefault();
    if (addBtn.disabled || addBtn.classList.contains('is-loading')) return;
    if (root.getAttribute('data-bp-needs-login') === 'true') return;

    var selectedCard = root.querySelector('.cd-bundle__card.is-selected') || root.querySelector('.cd-bundle__card');
    var selectedId = selectedCard ? parseInt(selectedCard.getAttribute('data-bp-variant-id'), 10) : 0;
    if (!selectedId) return;

    addBtn.classList.add('is-loading');
    addBtn.disabled = true;
    window._bundlePickerBusy = true;
    window.setFreebieLoading?.(true);

    bpAddToCart(selectedId)
      .then(function () {
        bpWriteFlag(BP_DISMISSED_KEY, null);
        bpWriteFlag(BP_CHOICE_KEY, String(selectedId));
        return new Promise(function (r) {
          setTimeout(r, 120);
        });
      })
      .then(function () {
        // Released before the refresh so the sync that follows it can repaint the picker.
        window._bundlePickerBusy = false;
        return bpRefresh();
      })
      .catch(function (err) {
        console.error('[BundlePicker] add failed', err);
        addBtn.classList.remove('is-loading');
        addBtn.disabled = false;
      })
      .finally(function () {
        window._bundlePickerBusy = false;
        window.setFreebieLoading?.(false);
      });
  }
});

// Keyboard selection: arrow keys move the radio without firing a click, so repaint on change too.
document.addEventListener('change', function (e) {
  var radio = e.target.closest('.cd-bundle__radio');
  if (!radio) return;
  var root = radio.closest('.cd-bundle');
  if (!root || root.classList.contains('is-locked')) return;
  if (root.classList.contains('is-below-threshold')) return;
  bpWriteFlag(BP_CHOICE_KEY, radio.value);
  bpRepaintAll();
});

document.addEventListener('keydown', function (e) {
  if (e.key === 'Escape') bpCloseTips(null);
});

// No cartUpdate subscriber here on purpose. cart.js runs the freebie handlers in one
// sequential chain, re-reading the cart between each, and calls this at the end of it.
// A second independent subscriber would race that chain - see the note at its call site.

// True while any of cart.js's freebie handlers is mid add/remove.
function bpFreebieBusy() {
  return !!(
    window.isGiftProductUpdating ||
    window._giftAddInProgress ||
    window.isRotatingGiftUpdating ||
    window._rotGiftInProgress ||
    window.isAutoProductUpdating ||
    window._autoAddInProgress
  );
}

document.addEventListener('DOMContentLoaded', function () {
  // Paint straight away from the server-rendered state.
  window.syncCartDrawerBundle();

  // Then take the bundle's turn at changing the cart, but only once cart.js's own
  // page-load freebie pass (syncAutoProductWithCart + handleRotatingGift) has had time to
  // run and settle. Those two are left exactly as they were; the bundle waits for them
  // rather than being woven into them, so page-load freebie behaviour is unchanged.
  var waited = 0;
  var timer = setInterval(function () {
    waited += 250;
    var settled = waited >= 1000 && !bpFreebieBusy();
    if (settled || waited >= 8000) {
      clearInterval(timer);
      window.syncCartDrawerBundle(null, true);
    }
  }, 250);
});
